import { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Space, Statistic, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Paragraph from 'antd/lib/typography/Paragraph';
import Title from 'antd/lib/typography/Title';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftMenuSettings from './LeftSettings';
import { BookyButton } from '../../Components/Buttons/BookyButton';
import { TextService } from '../../Services/TextService';
import { IntegrationService } from '../../Services/IntegrationService';
import { IntegrationType } from '../../Types/IntegrationType';
import {
    WebApiIntegrationsDtoMemberDataMismatchDto,
    WebApiIntegrationsDtoMemberStatusMismatchDto,
    WebApiIntegrationsDtoMemberSyncReportDto,
    WebApiIntegrationsDtoMissingMemberDto,
} from '../../Repository/eventbookyapi';

const mapMemberStatus = (status: number | undefined, GetText: (key: string) => string): string => {
    switch (status) {
        case 0:
            return GetText('MemberList_StatusActive');
        case 1:
            return GetText('MemberList_StatusCancelled');
        case 2:
            return GetText('MemberList_StatusUnconfirmedEmail');
        case 3:
            return GetText('MemberList_StatusUnconfirmedInitialPayment');
        default:
            return '-';
    }
};

const textOrDash = (value?: string | null) => {
    return value && value.length > 0 ? value : '-';
};

const FortnoxMismatchReport = () => {
    const GetText = TextService.GetText;
    const [report, setReport] = useState(null as WebApiIntegrationsDtoMemberSyncReportDto | null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const loadReport = async () => {
        setLoading(true);
        setError('');

        try {
            const result = await IntegrationService.GetMemberSyncReport(IntegrationType.Fortnox);
            setReport(result);
        } catch {
            setError(GetText('Integrations_MismatchReportLoadError'));
            setReport(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReport();
    }, []);

    const missingColumns: ColumnsType<WebApiIntegrationsDtoMissingMemberDto> = [
        {
            title: GetText('Integrations_MismatchReport_ColumnMemberId'),
            dataIndex: 'memberId',
            key: 'memberId',
            width: 100,
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnUniqueId'),
            dataIndex: 'uniqueId',
            key: 'uniqueId',
            width: 140,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnName'),
            dataIndex: 'fullName',
            key: 'fullName',
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnEmail'),
            dataIndex: 'email',
            key: 'email',
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnPhone'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnStatus'),
            dataIndex: 'status',
            key: 'status',
            width: 180,
            render: (value: number | undefined) => mapMemberStatus(value, GetText),
        },
    ];

    const dataMismatchColumns: ColumnsType<WebApiIntegrationsDtoMemberDataMismatchDto> = [
        {
            title: GetText('Integrations_MismatchReport_ColumnMemberId'),
            dataIndex: 'memberId',
            key: 'memberId',
            width: 100,
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnCustomerNumber'),
            dataIndex: 'customerNumber',
            key: 'customerNumber',
            width: 130,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnName'),
            dataIndex: 'fullName',
            key: 'fullName',
            width: 220,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnMismatchedFields'),
            dataIndex: 'mismatchedFields',
            key: 'mismatchedFields',
            render: (value: Array<string> | null | undefined) => (
                <Space wrap>
                    {(value ?? []).map((field) => (
                        <Tag key={field} color="orange">{field}</Tag>
                    ))}
                    {!value || value.length === 0 ? '-' : ''}
                </Space>
            ),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnValues'),
            key: 'values',
            render: (_, record) => (
                <div>
                    <div>{GetText('Integrations_MismatchReport_ValueEmail')}: {textOrDash(record.oneFrontEmail)} / {textOrDash(record.externalEmail)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValuePhone')}: {textOrDash(record.oneFrontPhone)} / {textOrDash(record.externalPhone)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueAddress1')}: {textOrDash(record.oneFrontAddress1)} / {textOrDash(record.externalAddress1)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueAddress2')}: {textOrDash(record.oneFrontAddress2)} / {textOrDash(record.externalAddress2)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueCity')}: {textOrDash(record.oneFrontCity)} / {textOrDash(record.externalCity)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueZip')}: {textOrDash(record.oneFrontZipCode)} / {textOrDash(record.externalZipCode)}</div>
                </div>
            ),
        },
    ];

    const statusMismatchColumns: ColumnsType<WebApiIntegrationsDtoMemberStatusMismatchDto> = [
        {
            title: GetText('Integrations_MismatchReport_ColumnMemberId'),
            dataIndex: 'memberId',
            key: 'memberId',
            width: 100,
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnCustomerNumber'),
            dataIndex: 'customerNumber',
            key: 'customerNumber',
            width: 130,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnName'),
            dataIndex: 'fullName',
            key: 'fullName',
            width: 220,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnOneFrontActive'),
            dataIndex: 'oneFrontIsActive',
            key: 'oneFrontIsActive',
            width: 140,
            render: (value: boolean | undefined) => value ? <Tag color="green">{GetText('Common_Yes')}</Tag> : <Tag color="red">{GetText('Common_No')}</Tag>,
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnExternalActive'),
            dataIndex: 'externalIsActive',
            key: 'externalIsActive',
            width: 140,
            render: (value: boolean | undefined) => value ? <Tag color="green">{GetText('Common_Yes')}</Tag> : <Tag color="red">{GetText('Common_No')}</Tag>,
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnOneFrontStatus'),
            dataIndex: 'oneFrontStatus',
            key: 'oneFrontStatus',
            width: 200,
            render: (value: number | undefined) => mapMemberStatus(value, GetText),
        },
    ];

    const mismatchCount = useMemo(() => {
        if (!report) {
            return 0;
        }

        return (report.missingInIntegration?.length ?? 0)
            + (report.dataMismatches?.length ?? 0)
            + (report.statusMismatches?.length ?? 0);
    }, [report]);

    return <LayoutLeftMenu title={GetText('Integrations_MismatchReportTitle')} menu={<LeftMenuSettings selected={'integrations'} />}>
        <Paragraph>{GetText('Integrations_MismatchReportDescription')}</Paragraph>

        {error ? <Alert style={{ marginTop: 12 }} type="error" message={error} showIcon /> : null}

        <Row gutter={16} style={{ marginTop: 20, marginBottom: 20 }}>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Statistic title={GetText('Integrations_MismatchReport_MembersInOneFront')} value={report?.totalMembers ?? 0} loading={loading} />
                </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Statistic title={GetText('Integrations_MismatchReport_MembersInFortnox')} value={report?.totalExternalMembers ?? 0} loading={loading} />
                </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Statistic title={GetText('Integrations_MismatchReport_TotalMismatches')} value={mismatchCount} loading={loading} />
                </Card>
            </Col>
        </Row>

        {report && mismatchCount === 0 ? <Alert type="success" showIcon message={GetText('Integrations_MismatchReportNoMismatches')} /> : null}

        <Title level={4} style={{ marginTop: 20 }}>{GetText('Integrations_MismatchReport_SectionMissingInIntegration')}</Title>
        <Table
            rowKey={(record) => `missing-${record.memberId}-${record.uniqueId}`}
            columns={missingColumns}
            dataSource={report?.missingInIntegration ?? []}
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '-' }}
            scroll={{ x: 900 }}
        />

        <Title level={4} style={{ marginTop: 20 }}>{GetText('Integrations_MismatchReport_SectionDataMismatches')}</Title>
        <Table
            rowKey={(record) => `data-${record.memberId}-${record.customerNumber}`}
            columns={dataMismatchColumns}
            dataSource={report?.dataMismatches ?? []}
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '-' }}
            scroll={{ x: 1400 }}
        />

        <Title level={4} style={{ marginTop: 20 }}>{GetText('Integrations_MismatchReport_SectionStatusMismatches')}</Title>
        <Table
            rowKey={(record) => `status-${record.memberId}-${record.customerNumber}`}
            columns={statusMismatchColumns}
            dataSource={report?.statusMismatches ?? []}
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '-' }}
            scroll={{ x: 1000 }}
        />
    </LayoutLeftMenu>;
};

export default FortnoxMismatchReport;
